import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/services/apiClient'
import type {
  Adapter,
  AdapterConnectResponse,
  AdapterRegisterRequest,
  AdapterTestRequest,
  AdapterTestResult,
} from '@/types/toolFramework'

export function useAdapters(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['adapters'],
    queryFn: () => apiClient.get<Adapter[]>('/api/adapters'),
    // Adapters router rides tool_framework — see useCapabilities.
    enabled: opts?.enabled ?? true,
  })
}

export function useConnectAdapter() {
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<AdapterConnectResponse>(`/api/adapters/${id}/connect`, {}),
  })
}

export function useTestAdapter() {
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & AdapterTestRequest) =>
      apiClient.post<AdapterTestResult>(`/api/adapters/${id}/test`, body),
  })
}

export function useDeleteAdapter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/adapters/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adapters'] })
    },
  })
}

export function useRegisterAdapter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AdapterRegisterRequest) =>
      apiClient.post<Adapter>('/api/adapters', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adapters'] })
    },
  })
}
